import paymentAPI from "./PaymentAPI";

/**
 * 결제 로직을 처리하는 서비스
 */
const PaymentService = {
    /**
     * 결제 요청을 처리합니다.
     * @param {Object} paymentForm - 결제 요청 데이터
     * @returns {Promise<Object>} - 결제 응답 데이터
     */
    async requestPayment(paymentForm) {
        // 간단하고 확실한 환경 감지 로직
        const userAgent = navigator.userAgent || '';
        const windowWidth = window.innerWidth;
        
        // 앱 환경 감지 (매우 엄격하게)
        const isInApp = typeof window.ReactNativeWebView !== 'undefined' || 
                        typeof window.AppInterface !== 'undefined' ||
                        /DayPay|daypay/i.test(userAgent);
        
        // 실제 모바일 기기 감지 (태블릿 제외)
        const isMobileDevice = /android.*mobile|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
        
        // PC 환경 우선 판단 - 화면이 크고 모바일 기기가 아니고 앱이 아닌 경우
        const isPC = windowWidth >= 1024 && !isMobileDevice && !isInApp;
        
        // 최종 모바일 여부 - PC가 아닌 경우에만 모바일로 처리
        const isMobile = !isPC;
        
        try {
            const response = await paymentAPI.RequestPayment(paymentForm);
        
            if (response.data.code === 200) {
                return {
                    success: true,
                    data: response.data.data,
                    isMobile
                };
            } else {
                return {
                    success: false,
                    message: response.data.message,
                    data: response.data
                };
            }
        } catch (error) {
            console.error('결제 처리 중 오류:', error);
            return {
                success: false,
                message: '결제 처리 중 오류가 발생했습니다.',
                error
            };
        }
    },

    /**
     * 앱 여부 판별 함수
     * @returns {boolean} - 앱 여부
     */
    isInApp() {
        // 1. User-Agent 확인
        const userAgent = navigator.userAgent || '';
        
        // 앱 관련 키워드들로 확장
        const isAppByUserAgent = /DayPay|daypay|AppWebView|wv|inapp|app/i.test(userAgent);
        
        // 2. ReactNativeWebView 객체 확인
        const hasReactNativeWebView = typeof window.ReactNativeWebView !== 'undefined';
        
        // 3. 앱 환경에서 주입되는 다른 객체나 메서드 확인
        const hasAppBridge = typeof window.AppInterface !== 'undefined' || 
                                typeof window.webkit?.messageHandlers?.AppInterface !== 'undefined';
        
        // 4. URL에서 앱 관련 파라미터 확인
        const isAppByUrl = window.location.href.includes('app=true') || 
                            window.location.href.includes('inapp=true');
        
        // 앱으로 판단되는 경우 true 반환
        return isAppByUserAgent || hasReactNativeWebView || hasAppBridge || isAppByUrl;
    },

    /**
     * 모바일 결제 처리 - 데이터 구조 개선
     */
    processMobilePayment(data, originUrl) {
        
        // 데이터 구조 확인 및 추출
        let paymentData = data;
        
        // data.formData가 있는 경우 해당 데이터 사용
        if (data.formData) {
            paymentData = data.formData;
        }
        
        // 주문번호 확인 (다양한 필드명 체크)
        const oid = paymentData.oid || paymentData.P_OID || paymentData.orderSeq || paymentData.order_seq;
        
        // 필수 데이터 검증
        if (!oid) {
            console.error('❌ 주문번호(oid)가 없습니다:', data);
            console.error('📋 전체 데이터 구조:', JSON.stringify(data, null, 2));
            alert('주문 정보가 올바르지 않습니다. 다시 시도해주세요.');
            return null;
        }
        
        // 결제 URL 확인
        const paymentUrl = data.paymentFormUrl || 'https://admin-daypay.kacelab.com/mobilePayRequest';
        
        // 결제 완료 후 리다이렉션할 URL 설정
        const returnUrl = `${originUrl}/payment/result`;
        
        try {
            // 현재 페이지 경로 저장 (결제 실패 시 돌아갈 경로)
            const currentPath = window.location.pathname;
            localStorage.setItem('payment_return_path', currentPath);
            localStorage.setItem('payment_order_id', oid);
            
            // 결제 진행 상태를 localStorage에 저장
            localStorage.setItem('payment_in_progress', 'true');
            localStorage.setItem('payment_start_time', Date.now().toString());
            
            // 동적으로 폼 생성
            const form = document.createElement('form');
            form.method="POST"
            form.acceptCharset="euc-kr"
            form.action = paymentUrl;
            form.style.display = 'none';
            
            // 폼 데이터 추가 - paymentData의 모든 필드 추가
            Object.entries(paymentData).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = String(value);
                    form.appendChild(input);
                }
            });
            
            // returnUrl 추가 (덮어쓰기)
            const returnUrlInput = document.createElement('input');
            returnUrlInput.type = 'hidden';
            returnUrlInput.name = 'returnUrl';
            returnUrlInput.value = returnUrl;
            form.appendChild(returnUrlInput);
            
            // paymethod가 없는 경우 추가
            if (!paymentData.paymethod) {
                const paymethodInput = document.createElement('input');
                paymethodInput.type = 'hidden';
                paymethodInput.name = 'paymethod';
                paymethodInput.value = 'Card';
                form.appendChild(paymethodInput);
            }
            
            // 폼을 DOM에 추가하고 제출
            document.body.appendChild(form);
            
            // 폼 제출
            form.submit();
            
            // 폼 제출 후 정리
            setTimeout(() => {
                if (document.body.contains(form)) {
                    document.body.removeChild(form);
                }
            }, 1000);
            
            return null;
        } catch (error) {
            console.error('❌ 모바일 결제 처리 중 오류:', error);
            alert('결제 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
            
            // 오류 발생 시 저장된 데이터 정리
            localStorage.removeItem('payment_return_path');
            localStorage.removeItem('payment_order_id');
            localStorage.removeItem('payment_in_progress');
            localStorage.removeItem('payment_start_time');
            
            return null;
        }
    },

    /**
     * 모바일 결제 결과를 처리합니다.
     * @param {Object} params - URL 쿼리 파라미터
     * @returns {Object} - 처리된 결제 데이터
     */
    processMobilePaymentResult(params) {
        // URL 쿼리 파라미터에서 결제 결과 추출
        if (!params) return null;
        
        const resultCode = params.get('resultCode') || '';
        
        // 결제 성공
        if (resultCode === '0000' || resultCode === 'success') {
            return {
                success: true,
                orderSeq: params.get('oid') || params.get('order_seq') || '',
                settleprice: params.get('price') || params.get('settleprice') || '0',
                orderKind: params.get('order_kind') || 'parcel'
            };
        } 
        // 결제 실패
        else if (resultCode) {
            return {
                success: false,
                message: '결제가 실패하였습니다.',
                resultCode,
                resultMsg: params.get('resultMsg') || ''
            };
        }
        
        return null;
    },

    /**
     * 모바일 환경에서 결제 결과 URL 파라미터를 확인합니다.
     * @returns {Object|null} - 결제 결과 데이터 또는 null
     */
    checkMobilePaymentResult() {
        // 현재 URL의 쿼리 파라미터 가져오기
        const queryParams = new URLSearchParams(window.location.search);
        
        // 결제 결과 코드가 있는지 확인
        const resultCode = queryParams.get('resultCode');
        
        if (resultCode) {
            return this.processMobilePaymentResult(queryParams);
        }
        
        return null;
    },

    /**
     * PC 결제창 URL을 생성합니다.
     * @param {Object} data - 결제 요청 응답 데이터
     * @param {string} originUrl - 현재 페이지의 origin
     * @returns {string} - 결제창 URL
     */
    createPaymentUrl(data, originUrl) {
        const queryParams = new URLSearchParams();
        
        Object.entries(data).forEach(([key, value]) => {
        queryParams.append(key, value);
        });
        
        // returnUrl을 App.js의 라우트 패턴에 맞게 설정
        const returnUrl = `${originUrl}/order/orderComplete/${data.oid}`;
        queryParams.append('returnUrl', returnUrl);

        queryParams.append('paymethod', 'Card');
        
        return `https://admin-daypay.kacelab.com/payRequest?${queryParams.toString()}`;
    },

    /**
     * 결제 완료 메시지를 처리합니다.
     * @param {MessageEvent} event - 메시지 이벤트
     * @returns {Object|null} - 처리된 결제 데이터 또는 null
     */
    handlePaymentMessage(event) {
        try {
        let resData = event.data;
        
        // 문자열이라면 JSON으로 변환 시도
        if (typeof resData === 'string') {
            try {
            resData = JSON.parse(resData);
            } catch (err) {
            console.log('JSON 파싱 실패, 원본 데이터 사용:', resData);
            }
        }
        
        // 결제창이 로드되는 메시지는 무시
        if (resData.type === 'load' || !resData.resultCode) {
            return null;
        }
        
        // 결제 성공
        if (resData.resultCode === '0000') {
            return {
            success: true,
            orderSeq: resData.oid || resData.order_seq,
            settleprice: resData.price || resData.settleprice,
            orderKind: resData.order_kind
            };
        } 
        // 결제 실패
        else if (resData.resultCode) {
            return {
            success: false,
            message: '결제가 실패하였습니다.',
            resultCode: resData.resultCode,
            resultMsg: resData.resultMsg
            };
        }
        
        return null;
        } catch (error) {
        console.error('결제 메시지 처리 중 오류:', error);
        return {
            success: false,
            message: '결제 메시지 처리 중 오류가 발생했습니다.',
            error
        };
        }
    },

    /**
     * 0원 결제 처리를 위한 데이터를 생성합니다.
     * @param {Object} data - 결제 응답 데이터
     * @returns {Object} - 0원 결제 완료 데이터
     */
    createZeroPaymentData(data) {
        return {
        success: true,
        orderSeq: data.oid,
        settleprice: 0,
        orderKind: data.order_kind
        };
    },

    /**
     * 모바일 기기 여부를 확인합니다.
     * @returns {boolean} - 모바일 기기 여부
     */
    isMobileDevice() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        
        // 매우 엄격한 모바일 감지 - 실제 모바일 기기만
        const mobileRegex = /android.*mobile|webos|iphone|ipod|blackberry|iemobile|opera mini/i;
        const isMobile = mobileRegex.test(userAgent.toLowerCase());
        
        // 태블릿과 데스크톱 제외
        const isTablet = /ipad|android(?!.*mobile)|tablet/i.test(userAgent.toLowerCase());
        const isDesktop = /windows|macintosh|linux|x11/i.test(userAgent.toLowerCase());
        
        const result = isMobile && !isTablet && !isDesktop;
        
        return result;
    },

    /**
     * 결제 전 유효성 검사를 수행합니다.
     * @param {Object} formData - 검증할 데이터
     * @returns {Object} - 유효성 검사 결과
     */
    validatePaymentForm(formData) {
        const errors = [];

        // 배송지 주소 검증
        if (formData.deliveryInfo) {
        if (!formData.deliveryInfo.recipient_zipcode || !formData.deliveryInfo.recipient_address_street) {
            errors.push('배송지 주소를 입력해주세요.');
        }
        }

        // 대여 주문인 경우에만 기간 체크
        if (formData.order_type === 'parcel' || formData.order_type === 'visit') {
        if (!formData.rental_sdate || !formData.rental_edate) {
            errors.push('대여 기간을 선택해주세요.');
        }
        }

        return {
        isValid: errors.length === 0,
        errors
        };
    },

    /**
     * 결제 폼 데이터를 생성합니다.
     * @param {Object} params - 결제 폼 데이터 생성을 위한 파라미터
     * @returns {Object} - 결제 요청에 필요한 폼 데이터
     */
    createPaymentForm({
        orderType,
        rentalStartDate,
        rentalEndDate,
        deliveryInfo,
        memo
    }) {
        return {
        order_seq: '',
        reorder_chk: 'N',
        order_type: orderType || 'parcel',
        rental_store_seq: '2',
        return_store_seq: '2',
        rental_sdate: rentalStartDate,
        rental_edate: rentalEndDate,
        download_seq: '',
        point: '0',
        shipping_cost: '3000',
        deposit_price: '100000',
        balance_cash_receipt: 'N',
        balance_cash_num: '',
        depositInfo: {
            deposit_refund_account: '',
            deposit_refund_mname: '',
            deposit_refund_name: '',
            deposit_refund_price: ''
        },
        deliveryInfo: {
            recipient_name: deliveryInfo?.recipient_name || '홍길동',
            recipient_phone: deliveryInfo?.recipient_phone || '01012345678',
            recipient_zipcode: deliveryInfo?.recipient_zipcode || '',
            recipient_address_street: deliveryInfo?.recipient_address_street || '',
            recipient_address_detail: deliveryInfo?.recipient_address_detail || '',
            recipient_memo: deliveryInfo?.recipient_memo || ''
        },
        estimateInfo: {
            undecidedChk: 'N',
            email: 'test@test.com',
            phone: '01012345678',
            phone2: '',
            business_name: '',
            business_number: '',
            project_manager_name: '',
            project_manager_phone: '',
            project_director: '',
            project_production: '',
            project_test_date: '',
            project_start_date: '',
            project_name: '',
            project_type: '',
            project_content: '',
            request_deposit: '',
            request_deposit_date: '',
            request_second_deposit: '',
            request_second_deposit_date: '',
            request_last_pay: '',
            request_last_pay_date: ''
        },
        user_memo: memo || '',
        pay_select_goods: ''
        };
    }
    };

export default PaymentService;
